import { useCallback, useRef, useState } from 'react';

export interface PanelEntry<T> {
  key: string;
  data: T;
  z: number;
}

/**
 * Generic keyed panel store: opening an existing key raises it to the front
 * (highest z) instead of duplicating it — used by FieldHistoryProvider to
 * dedupe/re-raise "View History" panels for the same PRO+field.
 */
export function usePanelStore<T>() {
  const [panels, setPanels] = useState<PanelEntry<T>[]>([]);
  const zCounter = useRef(0);

  const openPanel = useCallback((key: string, data: T) => {
    setPanels((prev) => {
      const nextZ = ++zCounter.current;
      const existing = prev.find((p) => p.key === key);
      if (existing) {
        return prev.map((p) => (p.key === key ? { ...p, z: nextZ } : p));
      }
      return [...prev, { key, data, z: nextZ }];
    });
  }, []);

  const closePanel = useCallback((key: string) => {
    setPanels((prev) => prev.filter((p) => p.key !== key));
  }, []);

  const raisePanel = useCallback((key: string) => {
    setPanels((prev) => {
      const nextZ = ++zCounter.current;
      return prev.map((p) => (p.key === key ? { ...p, z: nextZ } : p));
    });
  }, []);

  return { panels, openPanel, closePanel, raisePanel };
}
